import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useSquad } from "../contexts/SquadContext";
import type { Database } from "../lib/database.types";
import type { PostgrestError } from "@supabase/supabase-js";

type LeaderboardRow = Database["public"]["Views"]["leaderboard"]["Row"];

export function useLeaderboard(limit = 100) {
  const { mode, currentSquad } = useSquad();
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (mode === "global") {
        // Fetch global leaderboard
        const { data, error: fetchError } = await supabase
          .from("leaderboard")
          .select("*")
          .order("rank", { ascending: true })
          .limit(limit);

        if (fetchError) throw fetchError;
        setLeaderboard(data || []);
      } else if (currentSquad) {
        // Fetch squad-specific leaderboard
        // Type assertion needed because database.types.ts Functions may not be fully generated
        const { data, error: fetchError } = (await (supabase.rpc as any)(
          "get_squad_leaderboard",
          {
            squad_uuid: currentSquad.id,
          }
        )) as { data: LeaderboardRow[] | null; error: PostgrestError | null };

        if (fetchError) throw fetchError;
        // Apply limit after fetching since RPC functions don't support .limit()
        setLeaderboard((data || []).slice(0, limit));
      } else {
        setLeaderboard([]);
      }
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error("Failed to fetch leaderboard");
      setError(error);
      // eslint-disable-next-line no-console
      console.error("Failed to fetch leaderboard:", err);
    } finally {
      setLoading(false);
    }
  }, [limit, mode, currentSquad]);

  useEffect(() => {
    fetchLeaderboard();

    // Subscribe to real-time updates
    // Listen to both predictions (for new predictions) and profiles (for points/rank updates)
    const channel = supabase
      .channel("leaderboard-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "predictions",
        },
        () => {
          // Refetch leaderboard when predictions change
          fetchLeaderboard();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
        },
        () => {
          // Refetch leaderboard when profiles are updated (points, ranks, etc.)
          fetchLeaderboard();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchLeaderboard]);

  return { leaderboard, loading, error, refetch: fetchLeaderboard };
}
